import { useCallback, useMemo } from 'react';
import {
  addDays,
  format,
  fromUnixTime,
  min,
  parse,
} from 'date-fns';
import { store } from 'redux/store';
import { useSelector } from 'react-redux';
import {
  selectMeteorologyAgroWBCulturePeriodStartCP,
  selectMeteorologyAgroWBCulturePeriodEndCP,
  selectMeteorologyAgroWBCultureSelectedEmergencyDateCP,
  setPeriodStartCP,
  setPeriodEndCP,
} from 'redux/reducers/meteorologyAgroWBCultureReducer';
import useOptions from './useOptions';

export default function usePeriod() {
  const { options } = useOptions();
  const emergencyDate = useSelector(selectMeteorologyAgroWBCultureSelectedEmergencyDateCP);
  const periodStart = useSelector(selectMeteorologyAgroWBCulturePeriodStartCP);
  const periodEnd = useSelector(selectMeteorologyAgroWBCulturePeriodEndCP);
  const startDate = useMemo(
    () => (periodStart ? parse(periodStart, 'yyyy-MM-dd', new Date()) : null),
    [periodStart],
  );
  const endDate = useMemo(
    () => (periodEnd ? parse(periodEnd, 'yyyy-MM-dd', new Date()) : null),
    [periodEnd],
  );

  const dateIntervals = useMemo(() => {
    if (!options || !emergencyDate) return [];

    const start = parse(emergencyDate, 'yyyy-MM-dd', new Date());
    const emergencyEnd = fromUnixTime(options.endDate);
    const end = (startDate && !endDate)
      ? min([emergencyEnd, addDays(startDate, options.maxInterval - 1)])
      : emergencyEnd;

    return [{ start, end }];
  }, [options, emergencyDate, startDate, endDate]);

  const changePeriod = useCallback((newDates: (Date | null)[]) => {
    const [newStartDate, newEndDate] = newDates;
    const newPeriodStart = newStartDate ? format(newStartDate, 'yyyy-MM-dd') : undefined;
    const newPeriodEnd = newEndDate ? format(newEndDate, 'yyyy-MM-dd') : undefined;
    store.dispatch(setPeriodStartCP(newPeriodStart));
    store.dispatch(setPeriodEndCP(newPeriodEnd));
  }, []);

  return {
    startDate,
    endDate,
    dateIntervals,
    changePeriod,
  };
}
